import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { firestore } from '../config/firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  FiCopy, FiUsers, FiCheckCircle, FiAlertCircle, FiTrendingUp,
  FiClock, FiBarChart2, FiX, FiPhone, FiChevronRight, FiUser, FiSearch,
} from 'react-icons/fi';

const CACHE_KEY    = 'painel_cursos_cache';
const CACHE_TTL    = 60 * 60 * 1000;  // 1h painel
const DRAWER_TTL   = 15 * 60 * 1000;  // 15min participantes
const PAGE_SIZE    = 20;
const LIMIT_48H    = 48 * 60 * 60 * 1000;

const P = {
  pageBg:    '#eef4fb',
  sectionBg: '#c9dcf0',
  cardGreen: '#a8d5a2',
  cardRed:    '#f5c6cb',
  cardBlue:  '#a8c8f0',
  cardYellow:'#f5dfa2',
  text:      '#1a2a3a',
  muted:      '#4a6380',
  light:      '#6a8aaa',
};

// ─── Helpers de Formatação e Lógica ──────────────────────────────────────────

const parseSafeDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (ts) => {
  const d = parseSafeDate(ts);
  if (!d) return '—';
  return d.toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: 'short', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

const formatLastUpdate = (ts) => {
  if (!ts) return null;
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1)  return 'agora mesmo';
  if (m < 60) return `há ${m} min`;
  return `há ${Math.floor(m / 60)}h`;
};

const isDesengajado = (p) => {
  if (p.concluido) return false;
  const ultima = parseSafeDate(p.timestampUltimaEtapa);
  if (!ultima) return true;
  return (Date.now() - ultima.getTime()) > LIMIT_48H;
};

const formatContact = (id = '') => {
  const num = id.replace('@c.us', '').replace('@s.whatsapp.net', '');
  if (num.length >= 10) {
    const c = num.slice(0, 2), ddd = num.slice(2, 4), rest = num.slice(4);
    return `+${c} (${ddd}) ${rest.slice(0, -4)}-${rest.slice(-4)}`;
  }
  return num;
};

const rawNumber = (id = '') => id.replace('@c.us', '').replace('@s.whatsapp.net', '');

// ─── Estilos ─────────────────────────────────────────────────────────────────
const S = {
  root:    { fontFamily:"'DM Sans','Segoe UI',sans-serif", background:P.pageBg, minHeight:'100vh', color:P.text },
  wrapper: { maxWidth:'1280px', margin:'0 auto', padding:'32px 24px 64px' },
  pageHeader:  { display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:'28px', gap:'16px', flexWrap:'wrap' },
  pageTitle:   { fontSize:'clamp(1.2rem,3vw,1.6rem)', fontWeight:'800', letterSpacing:'-0.02em', color:P.text, margin:0, textTransform:'uppercase' },
  pageSubtitle:{ fontSize:'0.78rem', color:P.muted, marginTop:'5px', display:'flex', alignItems:'center', gap:'6px', fontWeight:'500' },
  dot:          { width:'7px', height:'7px', borderRadius:'50%', background:'#3d9e5f', display:'inline-block', animation:'pulse 2s infinite', flexShrink:0 },
  statsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:'14px', marginBottom:'24px' },
  statCard:  bg => ({ background:bg, border:'1px solid rgba(255,255,255,0.7)', borderRadius:'16px', padding:'22px 20px', display:'flex', alignItems:'center', gap:'14px', transition:'transform 0.15s,box-shadow 0.15s', boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }),
  statIcon:  c  => ({ width:'42px', height:'42px', borderRadius:'12px', background:'rgba(255,255,255,0.55)', display:'flex', alignItems:'center', justifyContent:'center', color:c, flexShrink:0 }),
  statLabel: { fontSize:'0.7rem', color:P.muted, textTransform:'uppercase', letterSpacing:'0.09em', fontWeight:'700', marginBottom:'3px' },
  statValue: { fontSize:'1.9rem', fontWeight:'800', color:P.text, lineHeight:1 },
  statHint:  { fontSize:'0.68rem', color:P.light, marginTop:'4px' },
  tableSection: { background:P.sectionBg, borderRadius:'20px', border:'1px solid rgba(255,255,255,0.5)', overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,0.07)' },
  tableHead:    { padding:'18px 24px', borderBottom:'1px solid rgba(255,255,255,0.4)', display:'flex', alignItems:'center', gap:'10px', background:'rgba(255,255,255,0.25)' },
  tableTitle:   { fontSize:'0.78rem', fontWeight:'800', color:P.text, textTransform:'uppercase', letterSpacing:'0.1em', margin:0 },
  tableScroll:  { overflowX:'auto', WebkitOverflowScrolling:'touch' },
  table:        { width:'100%', borderCollapse:'collapse', fontSize:'0.875rem', minWidth:'700px' },
  th: { padding:'11px 20px', textAlign:'left', fontSize:'0.68rem', fontWeight:'700', color:P.muted, textTransform:'uppercase', letterSpacing:'0.09em', background:'rgba(255,255,255,0.3)', borderBottom:'1px solid rgba(255,255,255,0.4)', whiteSpace:'nowrap' },
  td: { padding:'15px 20px', borderBottom:'1px solid rgba(255,255,255,0.35)', verticalAlign:'middle', background:'rgba(255,255,255,0.55)' },
  cursoNome:  { fontWeight:'700', color:P.text, marginBottom:'6px', fontSize:'0.9rem' },
  comandoRow: { display:'inline-flex', alignItems:'center', gap:'6px', cursor:'pointer', padding:'3px 9px', background:'rgba(255,255,255,0.8)', border:'1px solid rgba(100,160,220,0.3)', borderRadius:'6px', color:'#2c72b5', fontSize:'0.75rem', fontFamily:"'JetBrains Mono',monospace", transition:'all 0.15s', userSelect:'none' },
  badge:  ok => ({ display:'inline-flex', alignItems:'center', gap:'5px', padding:'4px 11px', borderRadius:'20px', fontSize:'0.72rem', fontWeight:'700', background:ok?P.cardGreen:P.cardRed, color:ok?'#1a5c2a':'#8b1a24' }),
  inscCol:    { display:'flex', flexDirection:'column', gap:'2px' },
  inscTotal:  { fontWeight:'800', color:P.text, fontSize:'1.05rem' },
  inscHoje:   { fontSize:'0.72rem', color:'#2a7a3a', fontWeight:'700' },
  progWrap:   { display:'flex', alignItems:'center', gap:'10px' },
  progLabel:  { fontSize:'0.8rem', fontWeight:'700', color:P.text, width:'34px', flexShrink:0, textAlign:'right' },
  progTrack:  { flex:1, height:'6px', background:'rgba(0,0,0,0.1)', borderRadius:'99px', overflow:'hidden', minWidth:'80px' },
  progFill:  p => ({ height:'100%', width:`${p}%`, background:p>60?'#3d9e5f':p>30?'#c8892a':'#c0394a', borderRadius:'99px', transition:'width 0.6s ease' }),
  aprovTag:  ok => ({ display:'inline-flex', alignItems:'center', padding:'5px 12px', borderRadius:'10px', fontSize:'0.78rem', fontWeight:'800', background:ok?P.cardGreen:P.cardRed, color:ok?'#1a5c2a':'#8b1a24' }),
  btnRel:    { display:'inline-flex', alignItems:'center', gap:'6px', padding:'7px 14px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.8)', background:'rgba(255,255,255,0.7)', color:P.muted, fontSize:'0.78rem', fontWeight:'600', cursor:'pointer', transition:'all 0.15s', whiteSpace:'nowrap' },
  overlay: open => ({ position:'fixed', inset:0, background:'rgba(15,30,50,0.35)', backdropFilter:'blur(2px)', zIndex:1000, opacity:open?1:0, pointerEvents:open?'all':'none', transition:'opacity 0.25s' }),
  drawer:  open => ({ position:'fixed', top:0, right:0, bottom:0, width:'min(520px,100vw)', background:'#f0f6fe', boxShadow:'-8px 0 40px rgba(0,0,0,0.14)', zIndex:1001, transform:open?'translateX(0)':'translateX(100%)', transition:'transform 0.3s cubic-bezier(0.32,0.72,0,1)', display:'flex', flexDirection:'column', overflow:'hidden' }),
  dHeader: { padding:'24px 56px 16px 24px', background:P.sectionBg, borderBottom:'1px solid rgba(255,255,255,0.5)', flexShrink:0, position:'relative' },
  dClose:  { position:'absolute', top:'20px', right:'20px', background:'rgba(255,255,255,0.7)', border:'none', borderRadius:'8px', width:'32px', height:'32px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:P.muted, transition:'all 0.15s' },
  dTitle:  { fontSize:'1rem', fontWeight:'800', color:P.text, margin:0 },
  dSub:    { fontSize:'0.75rem', color:P.muted, marginTop:'4px' },
  dSummary: { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', padding:'14px 24px', background:'rgba(255,255,255,0.35)', borderBottom:'1px solid rgba(255,255,255,0.45)', flexShrink:0 },
  sumCard:  bg => ({ background:bg, borderRadius:'12px', padding:'10px 14px', border:'1px solid rgba(255,255,255,0.6)' }),
  sumLabel: { fontSize:'0.62rem', fontWeight:'700', color:P.muted, textTransform:'uppercase', letterSpacing:'0.08em' },
  sumVal:   { fontSize:'1.4rem', fontWeight:'800', color:P.text, lineHeight:1, marginTop:'2px' },
  dProgBar: { padding:'12px 24px 8px', background:'rgba(255,255,255,0.2)', flexShrink:0 },
  searchWrap: { padding:'12px 24px', background:'rgba(255,255,255,0.3)', borderBottom:'1px solid rgba(255,255,255,0.4)', flexShrink:0 },
  searchBox:  { display:'flex', alignItems:'center', gap:'8px', background:'#ffffff', border:'1px solid rgba(180,210,240,0.6)', borderRadius:'10px', padding:'8px 12px' },
  searchInput:{ flex:1, border:'none', outline:'none', fontSize:'0.85rem', color:P.text, background:'transparent', fontFamily:"'DM Sans',sans-serif" },
  dBody:  { flex:1, overflowY:'auto', padding:'14px 24px 24px', display:'flex', flexDirection:'column', gap:'10px' },
  pCard:  { background:'#ffffff', borderRadius:'14px', padding:'16px', border:'1px solid rgba(180,210,240,0.4)', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', transition:'box-shadow 0.15s' },
  pHead:  { display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px' },
  avatar: bg => ({ width:'36px', height:'36px', borderRadius:'50%', background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }),
  pName:  { fontWeight:'700', fontSize:'0.88rem', color:P.text },
  pNum:    { display:'flex', alignItems:'center', gap:'5px', marginTop:'2px' },
  pMeta:  { display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'10px' },
  chip:    (bg,c) => ({ padding:'3px 9px', borderRadius:'20px', fontSize:'0.68rem', fontWeight:'700', background:bg, color:c }),
  pDate:  { marginTop:'10px', fontSize:'0.68rem', color:P.light },
  loadMore:  { margin:'4px 0 8px', padding:'10px', borderRadius:'10px', border:'1px solid rgba(180,210,240,0.5)', background:'rgba(255,255,255,0.6)', color:P.muted, fontSize:'0.82rem', fontWeight:'600', cursor:'pointer', width:'100%', transition:'all 0.15s' },
  empty:      { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:P.light, gap:'10px', padding:'40px', textAlign:'center' },
  dLoader:   { display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', color:P.muted, padding:'40px', fontSize:'0.88rem' },
  toast: ok => ({ position:'fixed', bottom:'24px', left:'50%', transform:`translateX(-50%) translateY(${ok?'0':'12px'})`, opacity:ok?1:0, transition:'all 0.25s', background:'#1a2a3a', borderRadius:'12px', padding:'10px 18px', fontSize:'0.82rem', color:'#e2eef8', pointerEvents:'none', zIndex:9999, display:'flex', alignItems:'center', gap:'8px', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }),
  loader:    { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:P.pageBg, color:P.muted, gap:'10px' },
};

const AVATAR_COLORS = [P.cardBlue, P.cardGreen, P.cardYellow, P.cardRed, '#c8b4f0', '#b4dff0'];

// ─── Drawer de Participantes ─────────────────────────────────────────────────
const DrawerParticipantes = ({ curso, open, onClose, tenantId }) => {
  const [participantes, setParticipantes] = useState([]);
  const [loadingP,      setLoadingP]      = useState(false);
  const [busca,          setBusca]          = useState('');
  const [pagina,        setPagina]        = useState(1);

  useEffect(() => {
    if (!open || !curso) return;
    setBusca('');
    setPagina(1);

    const cacheKey = `drawer_${tenantId}_${curso.id}`;
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const { data, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp < DRAWER_TTL) {
          setParticipantes(data);
          return;
        }
      }
    } catch (_) {}

    const fetch = async () => {
      setLoadingP(true);
      try {
        const snap = await getDocs(query(
          collection(firestore, 'progressoAlunos'),
          where('tenantId',    '==', tenantId),
          where('minicursoId', '==', curso.id),
        ));
        const data = snap.docs.map(d => d.data());
        sessionStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
        setParticipantes(data);
      } catch (e) { console.error(e); }
      finally { setLoadingP(false); }
    };
    fetch();
  }, [open, curso, tenantId]);

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return participantes;
    return participantes.filter(p => {
      const nome = (p.nome || '').toLowerCase();
      const num  = rawNumber(p.contactId || '');
      return nome.includes(t) || num.includes(t);
    });
  }, [participantes, busca]);

  const sorted = useMemo(() => [...filtrados].sort((a, b) => {
    const statusVal = p => p.concluido ? 0 : isDesengajado(p) ? 2 : 1;
    if (statusVal(a) !== statusVal(b)) return statusVal(a) - statusVal(b);
    return (b.ultimaEtapaConcluida || 0) - (a.ultimaEtapaConcluida || 0);
  }), [filtrados]);

  const visiveis  = sorted.slice(0, pagina * PAGE_SIZE);
  const temMais   = visiveis.length < sorted.length;

  const concluidos   = participantes.filter(p => p.concluido && p.statusAprovacao !== 'Interrompido').length;
  const desengajados = participantes.filter(isDesengajado).length;
  const conclusaoPct = participantes.length > 0 ? Math.round((concluidos / participantes.length) * 100) : 0;

  if (!curso) return null;

  return (
    <>
      <div style={S.overlay(open)} onClick={onClose}/>
      <div style={S.drawer(open)}>
        <div style={S.dHeader}>
          <button style={S.dClose} onClick={onClose} className="d-close"><FiX size={16}/></button>
          <p style={{ fontSize:'0.68rem', fontWeight:'700', color:P.light, textTransform:'uppercase', letterSpacing:'0.1em', margin:'0 0 4px' }}>Relatório do Curso</p>
          <h2 style={S.dTitle}>{curso.nome}</h2>
          <p style={S.dSub}>
            comando: <code style={{ fontFamily:"'JetBrains Mono',monospace", color:'#2c72b5' }}>{curso.comando}</code>
          </p>
        </div>

        {!loadingP && (
          <>
            <div style={S.dSummary}>
              <div style={S.sumCard(P.cardBlue)}>
                <div style={S.sumLabel}>Total</div>
                <div style={S.sumVal}>{participantes.length}</div>
              </div>
              <div style={S.sumCard(P.cardGreen)}>
                <div style={S.sumLabel}>Concluídos</div>
                <div style={S.sumVal}>{concluidos}</div>
              </div>
              <div style={S.sumCard(P.cardYellow)}>
                <div style={S.sumLabel}>Desengajados</div>
                <div style={S.sumVal}>{desengajados}</div>
              </div>
            </div>
            <div style={S.dProgBar}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.72rem', color:P.muted, fontWeight:'600', marginBottom:'6px' }}>
                <span>Conclusão geral</span>
                <span style={{ fontWeight:'800', color:P.text }}>{conclusaoPct}%</span>
              </div>
              <div style={{ height:'7px', background:'rgba(0,0,0,0.08)', borderRadius:'99px', overflow:'hidden' }}>
                <div style={S.progFill(conclusaoPct)}/>
              </div>
            </div>
          </>
        )}

        <div style={S.searchWrap}>
          <div style={S.searchBox}>
            <FiSearch size={14} color={P.light}/>
            <input
              style={S.searchInput}
              placeholder="Buscar por nome ou número…"
              value={busca}
              onChange={e => { setBusca(e.target.value); setPagina(1); }}
            />
            {busca && (
              <button onClick={() => setBusca('')} style={{ background:'none', border:'none', cursor:'pointer', color:P.light, display:'flex', padding:0 }}>
                <FiX size={13}/>
              </button>
            )}
          </div>
        </div>

        <div style={S.dBody}>
          {loadingP ? (
            <div style={S.dLoader}><FiUsers size={18} opacity={0.4}/> Carregando participantes…</div>
          ) : visiveis.length === 0 ? (
            <div style={S.empty}>
              <FiUsers size={32} opacity={0.25}/>
              <p style={{ margin:0, fontSize:'0.85rem' }}>{busca ? 'Nenhum resultado encontrado.' : 'Nenhum participante ainda.'}</p>
            </div>
          ) : (
            <>
              {visiveis.map((p, i) => {
                const totalResp = (p.acertos || 0) + (p.erros || 0);
                const aprovPct  = totalResp > 0 ? Math.round(((p.acertos || 0) / totalResp) * 100) : 0;
                const isDeseng  = isDesengajado(p);

                return (
                  <div key={p.contactId || i} style={S.pCard} className="p-card">
                    <div style={S.pHead}>
                      <div style={S.avatar(AVATAR_COLORS[i % AVATAR_COLORS.length])}>
                        <FiUser size={15} color={P.muted}/>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: '800', 
                          fontSize: '1rem', 
                          color: P.text,
                          marginBottom: '2px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {p.pushname || p.pushName || p.nome || 'Usuário'}
                        </div>
                        <div style={S.pNum}>
                          <FiPhone size={11} color={P.light}/>
                          <span style={{ fontSize: '0.75rem', color: P.muted, fontFamily: "'JetBrains Mono',monospace" }}>
                            ({formatContact(p.contactId)})
                          </span>
                        </div>
                      </div>
                      {p.statusAprovacao === 'Interrompido'
                        ? <span style={S.chip('rgba(0,0,0,0.06)', P.muted)}>Interrompido</span>
                        : p.concluido
                        ? <span style={S.chip(P.cardGreen,'#1a5c2a')}>✓ Concluído</span>
                        : isDeseng  
                          ? <span style={S.chip(P.cardYellow,'#7a4a10')}>Desengajado</span>
                          : <span style={S.chip(P.cardBlue,'#1a5080')}>Ativo</span>
                      }
                    </div>
                    <div style={S.pMeta}>
                      <span style={S.chip('rgba(0,0,0,0.06)', P.muted)}>Etapa {p.ultimaEtapaConcluida || 0}</span>
                      <span style={S.chip('rgba(0,0,0,0.06)', P.muted)}>✓ {p.acertos || 0} acertos</span>
                      <span style={S.chip('rgba(0,0,0,0.06)', P.muted)}>{aprovPct}% aproveitamento</span>
                    </div>
                    <div style={S.pDate}>
                      Inscrito em {formatDate(p.timestampInscricao)}
                      {p.timestampUltimaEtapa && <> · última atividade {formatDate(p.timestampUltimaEtapa)}</>}
                    </div>
                  </div>
                );
              })}
              {temMais && (
                <button className="load-more" style={S.loadMore} onClick={() => setPagina(pg => pg + 1)}>
                  Carregar mais ({sorted.length - visiveis.length} restantes)
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Componente Principal ────────────────────────────────────────────────────
const PainelEstrategico = () => {
  const tenantId = localStorage.getItem('tenantId');
  const [minicursos, setMinicursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [toast, setToast] = useState({ visible: false, msg: '' });
  const [drawerCurso, setDrawerCurso] = useState(null);

  const showToast = (msg) => {
    setToast({ visible: true, msg });
    setTimeout(() => setToast({ visible: false, msg: '' }), 2200);
  };

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!tenantId) return;
    const cacheKey = `${CACHE_KEY}_${tenantId}`;

    if (!forceRefresh) {
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const { cursos, timestamp } = JSON.parse(raw);
          if (Date.now() - timestamp < CACHE_TTL) {
            setMinicursos(cursos); setLastUpdate(timestamp); setLoading(false);
            return;
          }
        }
      } catch (_) {}
    }

    setLoading(true);
    try {
      const [csSnap, psSnap] = await Promise.all([
        getDocs(query(collection(firestore, 'minicursos'), where('tenantId', '==', tenantId))),
        getDocs(query(collection(firestore, 'progressoAlunos'), where('tenantId', '==', tenantId))),
      ]);

      const cursos = csSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const progresso = psSnap.docs.map(d => d.data());
      const hojeStr = new Date().toLocaleDateString('pt-BR');

      const processados = cursos.map(curso => {
        const alunos = progresso.filter(p => p.minicursoId === curso.id);
        
        const inscritosHoje = alunos.filter(p => {
          const d = parseSafeDate(p.timestampInscricao);
          return d && d.toLocaleDateString('pt-BR') === hojeStr;
        }).length;

        const parados = alunos.filter(isDesengajado).length;
        const totalRespostas = alunos.reduce((a, p) => a + (p.acertos || 0) + (p.erros || 0), 0);
        const totalAcertos = alunos.reduce((a, p) => a + (p.acertos || 0), 0);
        const aproveitamento = totalRespostas > 0 ? Math.round((totalAcertos / totalRespostas) * 100) : 0;

        return { 
          ...curso, 
          inscritosHoje, 
          total: alunos.length, 
          concluidos: alunos.filter(p => p.concluido && p.statusAprovacao !== 'Interrompido').length, 
          parados, 
          aprov: aproveitamento,
          _totalAcertos: totalAcertos,
          _totalRespostas: totalRespostas
        };
      });

      const now = Date.now();
      localStorage.setItem(cacheKey, JSON.stringify({ cursos: processados, timestamp: now }));
      setMinicursos(processados); setLastUpdate(now);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
    const t = setInterval(() => fetchData(true), CACHE_TTL);
    return () => clearInterval(t);
  }, [fetchData]);

  const copiarComando = (txt) => { navigator.clipboard.writeText(txt); showToast(`Comando "${txt}" copiado!`); };

  // --- AJUSTE SOLICITADO: Contagem de cursos ativos ---
  const cursosAtivosCount = useMemo(() => {
    return minicursos.filter(curso => 
      ['ativo', 'active'].includes(curso.status?.toLowerCase())
    ).length;
  }, [minicursos]);

  // Cálculos consolidados para os cards superiores
  const totalInscritosGlobal = minicursos.reduce((a, c) => a + c.total, 0);
  const totalInscritosHojeGlobal = minicursos.reduce((a, c) => a + c.inscritosHoje, 0);
  const gA = minicursos.reduce((a, c) => a + (c._totalAcertos || 0), 0);
  const gR = minicursos.reduce((a, c) => a + (c._totalRespostas || 0), 0);
  const aprovGlobal = gR > 0 ? Math.round((gA / gR) * 100) : 0;

  const STATS = [
    { 
      icon: <FiUsers size={18}/>, 
      label: 'Inscritos', 
      value: totalInscritosGlobal, 
      bg: P.cardBlue, 
      ic: '#1a5080', 
      hint: `Total acumulado (${totalInscritosHojeGlobal} novos hoje)` 
    },
    { icon: <FiCheckCircle size={18}/>, label: 'Concluintes', value: minicursos.reduce((a, c) => a + c.concluidos, 0), bg: P.cardGreen, ic: '#1a5c2a', hint: 'Total acumulado' },
    { icon: <FiAlertCircle size={18}/>, label: 'Desengajados', value: minicursos.reduce((a, c) => a + c.parados, 0), bg: P.cardYellow, ic: '#7a4a10', hint: 'Inativos > 48h' },
    { icon: <FiTrendingUp size={18}/>, label: 'Performance', value: `${aprovGlobal}%`, bg: P.cardRed, ic: '#8b1a24', hint: 'Média de acertos global' },
  ];

  if (loading) return <div style={S.loader}><FiBarChart2 size={20}/>Carregando dados estratégicos…</div>;

  return (
    <div style={S.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        .scard:hover{transform:translateY(-2px);box-shadow:0 6px 16px rgba(0,0,0,0.12)!important}
        tr.rh:hover td{background:rgba(255,255,255,0.78)!important}
        .p-card:hover{box-shadow:0 4px 16px rgba(0,0,0,0.1)!important}
      `}</style>

      <div style={S.wrapper}>
        <div style={S.pageHeader}>
          <div>
            {/* Título dinâmico baseado na quantidade de cursos ativos */}
            <h1 style={S.pageTitle}>
              {cursosAtivosCount} {cursosAtivosCount === 1 ? 'CURSO' : 'CURSOS'}
            </h1>
      <div style={S.pageSubtitle}>
  <span style={S.dot}/>
  Monitoramento de Performance
  {lastUpdate && <span style={{marginLeft:'8px',color:P.light}}>· atualizado {formatLastUpdate(lastUpdate)}</span>}
</div>
          </div>
        </div>

        <div style={S.statsGrid}>
          {STATS.map(s => (
            <div key={s.label} className="scard" style={S.statCard(s.bg)} title={s.hint}>
              <div style={S.statIcon(s.ic)}>{s.icon}</div>
              <div>
                <div style={S.statLabel}>{s.label}</div>
                <div style={S.statValue}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={S.tableSection}>
          <div style={S.tableHead}>
            <FiBarChart2 size={14} color={P.muted}/>
            <h2 style={S.tableTitle}>Minicursos Disponíveis</h2>
          </div>
          <div style={S.tableScroll}>
            <table style={S.table}>
              <thead>
                <tr>{['Curso / Comando','Status','Inscritos','Conclusão','Ações'].map(h=>(
                  <th key={h} style={S.th}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {minicursos.map(curso => {
                  const pct = Math.round((curso.concluidos / curso.total) * 100 || 0);
                  const isOk = ['ativo','active'].includes(curso.status?.toLowerCase());
                  return (
                    <tr key={curso.id} className="rh">
                      <td style={S.td}>
                        <div style={S.cursoNome}>{curso.nome}</div>
                        <div className="ctag" style={S.comandoRow} onClick={() => copiarComando(curso.comando)}>
                          <code>{curso.comando}</code><FiCopy size={11}/>
                        </div>
                      </td>
                      <td style={S.td}>
                        <span style={S.badge(isOk)}>{curso.status}</span>
                      </td>
                      <td style={S.td}>
                        <div style={S.inscCol}>
                          <span style={S.inscTotal}>{curso.total}</span>
                          {curso.inscritosHoje > 0 && <span style={S.inscHoje}>+{curso.inscritosHoje} hoje</span>}
                        </div>
                      </td>
                      <td style={S.td}>
                        <div style={S.progWrap}>
                          <span style={S.progLabel}>{pct}%</span>
                          <div style={S.progTrack}><div style={S.progFill(pct)}/></div>
                        </div>
                      </td>
                      <td style={S.td}>
                        <button className="brel" style={S.btnRel} onClick={() => setDrawerCurso(curso)}>
                          Ver alunos <FiChevronRight size={13}/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={S.toast(toast.visible)}><FiCopy size={12}/>{toast.msg}</div>

      <DrawerParticipantes
        curso={drawerCurso}
        open={!!drawerCurso}
        onClose={() => setDrawerCurso(null)}
        tenantId={tenantId}
      />
    </div>
  );
};

export default PainelEstrategico;
